import CTNCheckbox from '@ctn/ui/components/ctn-checkbox';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNModal from '@ctn/ui/components/ctn-modal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNTextArea from '@ctn/ui/components/ctn-textarea';
import { type CTNModalProps } from '@ctn/ui/types/ctn-modal';
import classNames from 'classnames';
import { useEffect, useState, type ReactElement } from 'react';
import { FormProvider, useForm, type Resolver, type SubmitErrorHandler } from 'react-hook-form';

import { PersonaFisica, PersonaJuridica, TipoInterviniente } from '@/components/Interviniente';
import {
	getDefaultValues,
	resolver,
	type SujetoForm,
	schema as schemaInterviniente,
} from '@/components/Interviniente/config';
import { useGlobalContext } from '@/context/global.context';
import useApiData from '@/hooks/useApiData';
import {
	getDetailSubsanaciones,
	postSubsanacionesErroresSujeto,
	postSubsanacionesSinErrores,
} from '@/services/tramite.service';

import { ERROR_TYPE } from '@/constants/errors.constants';
import { TIPO_PERSONA } from '@/constants/tramite.constants';
import { mapSujetoDBToForm, mapSujetoFormToDB } from '@/helpers/formSujeto';
import { notificationsConfig } from '@/helpers/notifications';
import { peekErrors } from '@/helpers/validations';
import { type ISubsanacion, type ISubsanacionSujeto } from '@/interfaces/model/don.interface';

import { defaultValues as defaultValuesGlobal, getSchema, schema, type Form } from './form';

type ModalParams = {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly onSubmit: () => void;
	readonly data: {
		idTramite: ISubsanacion['tramite']['id'];
		idTipo: ISubsanacion['tipo']['id'];
		id: ISubsanacion['id'];
	};
};

const ModalSubsanacionInterviniente = ({
	isOpen,
	onClose,
	data: dataSubsanacion,
	onSubmit: callbackSubmit,
}: ModalParams): ReactElement => {
	const {
		maestrosTramite: { tipoDocumentoIdent, tipoPersona, tipoIntervencion },
		maestrosGeneric: { tipoDireccion },
		user,
	} = useGlobalContext();
	const [isHidden, setIsHidden] = useState(false);
	const [hasMissingRequired, setHasMissingRequired] = useState<boolean>(true);
	const [disabledFormSujeto, setDisabledFormSujeto] = useState(false);
	const [disableActions, setDisableActions] = useState(false);
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { loading, triggerFetch } = useApiData({ loading: ['detail', 'subsanar'] as const });

	const methodsSujeto = useForm<SujetoForm>({
		mode: 'onSubmit',
		resolver: resolver({}),
		defaultValues: getDefaultValues(),
	});

	const {
		control,
		reset,
		handleSubmit,
		clearErrors,
		watch,
		formState: { errors },
	} = useForm<Form>({
		defaultValues: defaultValuesGlobal,
		resolver: getSchema() as Resolver<Form>,
		mode: 'onChange',
	});

	const formData = watch();
	const [notSubsanacion] = watch('notSubsanacion') ?? [];
	const isSubsanacion = notSubsanacion !== 'true';

	// Estado para indicar es necesario aplicar cambios en el sujeto ya que se necesita una subsanación.
	const isRequiredChangeOnSujeto = isSubsanacion && !methodsSujeto.formState.isDirty;

	const mapData = (data: Omit<ISubsanacionSujeto, 'sujetoErroneo'>): Form => {
		const { tramite, id, tipo, fechaCreacion, observacionesPeticion } = data;

		return {
			...getDefaultValues(),
			nifEmisor: tramite?.nifEmisor,
			idError: id,
			idTramite: tramite?.id,
			idTipoError: tipo?.id,
			numeroTramite: tramite.numeroTramite,
			fechaCreacion,
			observacionesPeticion,
			observacionesEmisor: '',
			notSubsanacion: [],
		};
	};

	const idTipoPersona = methodsSujeto.watch('idTipoPersona');
	const isPersonaFisica = idTipoPersona === String(TIPO_PERSONA.FISICA);
	const isPersonaJuridica = idTipoPersona === String(TIPO_PERSONA.JURIDICA);

	const onSuccess = () => {
		openNotification(
			notificationTemplate.successMessage(
				'Interviniente subsanado',
				'El interviniente ha sido subsanado correctamente.',
			),
		);
		callbackSubmit();
		onClose();
	};

	const onSubmitWithSubsanacion =
		({ observacionesEmisor, nifEmisor, idError, idTramite, idTipoError }: Form) =>
			async (sujetoForm: SujetoForm) => {
				const sujeto = mapSujetoFormToDB(sujetoForm, {
					tipoDocumentoIdent,
					tipoIntervencion,
					tipoPersona,
					tipoDireccion,
				});

				if (!user) return;

				const payloadCommon = {
					observacionesEmisor,
					nifEmisor,
					idError,
					idTramite,
					idTipoError,
					tipoSector: user?.tipoSectorEmisor,
				};

				const { code } = await triggerFetch({
					fetch: postSubsanacionesErroresSujeto,
					payload: { ...payloadCommon, sujeto },
					name: 'subsanar',
					error: (
						<>
							<p>Se ha producido un error en la subsanación del interviniente.</p>
							<p> Por favor, vuelva a intentarlo</p>
						</>
					),
					onCloseError: () => onClose(),
				});
				if (code === 200) {
					onSuccess();
				} else {
					onClose();
				}
			};

	const onSubmitNotSubsanacion = async ({
		observacionesEmisor,
		nifEmisor,
		idError,
		idTramite,
		idTipoError,
	}: Form) => {
		if (!user) return;

		const payload = {
			observacionesEmisor,
			nifEmisor,
			idError,
			idTramite,
			idTipoError,
			tipoSector: user?.tipoSectorEmisor,
		};

		const { code } = await triggerFetch({
			fetch: postSubsanacionesSinErrores,
			payload,
			name: 'subsanar',
			error: (
				<>
					<p>Se ha producido un error en la subsanación del interviniente.</p>
					<p> Por favor, vuelva a intentarlo</p>
				</>
			),
			onCloseError: () => onClose(),
		});
		if (code === 200) {
			onSuccess();
		} else {
			onClose();
		}
	};

	const onInvalid: SubmitErrorHandler<SujetoForm> = (errors) => {
		if (errors?.representantes) {
			setIsHidden(true);
			const onClose = () => setIsHidden(false);
			openNotification(notificationTemplate.erroresForm(errors.representantes.message as string, onClose));
		}
	};

	const onSubmit = async (data: Form) => {
		const isSubsanable = data.notSubsanacion[0] !== 'true';

		if (isSubsanable) {
			// Si es subsanable se requiere validar todos los campos de sujeto.
			methodsSujeto.handleSubmit(onSubmitWithSubsanacion(data), onInvalid)();
		} else {
			onSubmitNotSubsanacion(data);
		}
	};

	const actionButtons: CTNModalProps['actionButtons'] = [
		{
			key: 'tertiary',
			mode: 'tertiary',
			label: 'Cancelar',
			size: 'size-40',
			minWidth: '140',
			disabled: disableActions,
			onClick: onClose,
		},
		{
			key: 'primary',
			mode: 'primary',
			label: 'Subsanar',
			minWidth: '140',
			size: 'size-40',
			loading: loading.subsanar,
			/** Se aplica disabled cuando:
			 * - El componente padre deshactiva los botones (disabledActions)
			 * - Cuando carga los datos (loading.detail)
			 * - Cuando tienes campos obligatorios por rellenar (hasMissingRequired)
			 * - Cuando no se han realizado cambios en el sujeto y son necesarios (es una subsanación)
			 */
			disabled: disableActions || loading.detail || hasMissingRequired || isRequiredChangeOnSujeto,
			onClick(e) {
				handleSubmit(onSubmit)(e);
			},
		},
	];

	const getData = async () => {
		if (!user) return;

		const response = await triggerFetch({
			fetch: getDetailSubsanaciones,
			payload: {
				nifEmisor: user?.nifEmisor,
				idTramite: dataSubsanacion.idTramite,
				idTipoSector: user?.tipoSectorEmisor.id,
				idTipoSubsanacion: dataSubsanacion.idTipo,
				idSubsanacion: dataSubsanacion.id,
			},
			name: 'detail',
		});

		const { sujetoErroneo, ...rest } = response.data as ISubsanacionSujeto;

		if (response.data) {
			methodsSujeto.reset(mapSujetoDBToForm(sujetoErroneo));
			reset(mapData(rest));
		}
	};

	const resetModal = () => {
		setHasMissingRequired(true);
		setDisabledFormSujeto(false);
		setDisableActions(false);
		setIsHidden(false);
	};

	useEffect(() => {
		const checkRequiredFields = async () => {
			// Unificar los errores del intervinientes como los campos de subsanación
			// Comprobar si algunos de los campos falla la validación de requerido.
			const errorsFormSubsanacion = await peekErrors({
				schema,
				data: formData
			});

			const errorsFormInterviniente = await peekErrors({
				schema: schemaInterviniente({}),
				data: methodsSujeto.watch()
			});

			const hasErrorsRequired = [...errorsFormSubsanacion, ...errorsFormInterviniente].some(
				({ type }) => type === ERROR_TYPE.required,
			);
			
			setHasMissingRequired(hasErrorsRequired);
		};

		checkRequiredFields();
	}, [formData, schema]);

	useEffect(() => {
		if (!isOpen) {
			resetModal();
			return;
		}

		getData();
	}, [isOpen]);

	return (
		<CTNModal
			autoHeight
			closeable
			className={classNames('c-pnp-modal', 'c-pnp-party-amendment', { 'u-hidden': isHidden })}
			type="basic"
			title="Subsanar interviniente"
			size="xxl"
			alignButtons="right"
			actionButtons={actionButtons}
			show={isOpen}
			content={
				<div className="[ o-grid ] [ c-pnp-form ]">
					<div className="[ o-grid ] [ c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--radius c-pnp-form-group--display-only ] [ u-flow ]">
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="o-grid__col-12 o-grid__col-4@portrait"
								name="fechaCreacion"
								label="Fecha alta"
								control={control}
							/>
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="[ o-grid__col-12 o-grid__col-4@portrait ]"
								name="numeroTramite"
								label="Núm solicitud"
								control={control}
							/>
						</div>
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="o-grid__col-12"
								name="observacionesPeticion"
								label="Motivo de la subsanación"
								control={control}
							/>
						</div>
					</div>
					<div className="[ c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--white c-pnp-form-group--radius c-pnp-form-group--display-only ] [ u-flow ]">
						<CTNCheckbox
							noFeedBack
							control={control}
							checkbox={{ large: true }}
							items={[
								{
									label: 'No es necesario realizar cambios en los datos del interviniente',
									value: 'true',
									icon: {
										icon: 'help',
										color: 'blue-50',
										tooltip: {
											place: 'left',
											maxWidth: 300,
											content:
												'Al activar el check, se deberá indicar los motivos en la sección de Observaciones',
										},
									},
								},
							]}
							name="notSubsanacion"
							onChange={() => {
								setDisabledFormSujeto((value) => !value);
								clearErrors();
							}}
						/>
					</div>
					<FormProvider {...methodsSujeto}>
						<TipoInterviniente isDisplay className="[ p-detail-parties ] [ u-mt-24 ]" />
						{isPersonaFisica && <PersonaFisica isDisabled={disabledFormSujeto} className="u-mt-24" />}
						{isPersonaJuridica && (
							<PersonaJuridica
								isDisabled={disabledFormSujeto}
								setDisableActions={setDisableActions}
								className="u-mt-24"
								classNameRepresentante="c-pnp-party-amendment__representative"
							/>
						)}
					</FormProvider>
					<fieldset>
						<legend className="c-pnp-heading-h4">Observaciones sobre la solicitud</legend>
						<CTNTextArea
							classNameField="u-mt-16"
							required={!isSubsanacion}
							name="observacionesEmisor"
							label="Observaciones"
							control={control}
							maxLength={2000}
							error={errors.observacionesEmisor?.message}
							placeholder="Indicar aquí las observaciones."
							message={`${formData?.observacionesEmisor?.length}/2000`}
						/>
					</fieldset>
				</div>
			}
			onClose={onClose}
		/>
	);
};

export default ModalSubsanacionInterviniente;
